import { default as dayjs } from "dayjs";
import { default as relativeTime } from "dayjs/plugin/relativeTime.js";
import { default as duration } from "dayjs/plugin/duration.js";

dayjs.extend(relativeTime);
dayjs.extend(duration);

const langs =
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  require(`./dateTimeFormatter/langs/${process.env.VUE_APP_INSTANCE}`).default;

type DurationUnits =
  | null
  | "seconds"
  | "minutes"
  | "hours"
  | "days"
  | "months"
  | "years";
export const formatDuration = (
  timeInSeconds: number,
  unit: DurationUnits = null
) => {
  if (!langs) {
    throw new Error("langs not loaded");
  }

  if (unit === null) {
    return dayjs.duration(timeInSeconds, "second").humanize();
  }

  const durationAsUnit = dayjs.duration(timeInSeconds, "second").as(unit);

  switch (unit) {
    case "seconds":
      return langs.relativeTime.s;
    case "minutes":
      return durationAsUnit === 1
        ? langs.relativeTime.m
        : langs.relativeTime.mm.replace(/%d/, durationAsUnit);
    case "hours":
      return durationAsUnit === 1
        ? langs.relativeTime.h
        : langs.relativeTime.hh.replace(/%d/, durationAsUnit);
    case "days":
      return durationAsUnit === 1
        ? langs.relativeTime.d
        : langs.relativeTime.dd.replace(/%d/, durationAsUnit);
    case "months":
      return durationAsUnit === 1
        ? langs.relativeTime.M
        : langs.relativeTime.MM.replace(/%d/, durationAsUnit);
    case "years":
      return durationAsUnit === 1
        ? langs.relativeTime.y
        : langs.relativeTime.yy.replace(/%d/, durationAsUnit);
  }
};

export const formatMinutesForHumans = (minutes: number) => {
  if (!langs) {
    throw new Error("langs not loaded");
  }

  if (minutes < 60) {
    return `${minutes}${langs.minutes_short}`;
  }

  const hours = Math.floor(minutes / 60);
  const minutesLeft = minutes - hours * 60;

  if (!minutesLeft) {
    return `${hours}${langs.hours_short}`;
  }

  return `${hours}${langs.hours_short} ${minutesLeft}${langs.minutes_short}`;
};

export const formatMinutesPerEpisode = (minutesPerEpisode: number) => {
  return `${minutesPerEpisode}min/ep.`;
};

export const formatDate = (timestamp: number, typeFormat = "") => {
  return dayjs.unix(timestamp).format(typeFormat);
};

export const formatDateLong = (
  timestamp: number,
  lang = "es",
  withYear = false
) => {
  if (!langs) {
    throw new Error("langs not loaded");
  }

  const date = dayjs.unix(timestamp);

  // Fix certain formatting options for cat
  const fixMonthNameForCAT = (str: string) => {
    if (lang !== "cat") {
      return str;
    }

    const monthStartsWithVowel = /^\d+ [AEIOU]/i.test(str);

    return str.replace(/^(\d+) /, monthStartsWithVowel ? "$1 d'" : "$1 de ");
  };

  if (Math.abs(dayjs().diff(date, "month")) > 3) {
    // Return format with year
    return fixMonthNameForCAT(date.format(langs.formats.LL));
  }

  return fixMonthNameForCAT(
    date.format(!withYear ? langs.customFormats.Ll : langs.customFormats.Lwy)
  );
};

export const formatDateShort = (timestamp: number) => {
  return formatDate(timestamp, "DD/MM/YY");
};

export const formatDateTimeRelativeForHumans = (
  timestamp: number,
  preffix = false
) => {
  const date = dayjs.unix(timestamp);
  if (Math.abs(dayjs().diff(date, "month")) > 11) {
    return formatDateLong(timestamp);
  }
  return date.fromNow(!preffix);
};

/** Format a period between to dates, can apply smart optimizations like not showing year twice */
export const formatDateRange = (
  timestampStart: number,
  timestampEnd: number
) => {
  const dateStart = dayjs.unix(timestampStart);
  const dateEnd = dayjs.unix(timestampEnd);
  const now = dayjs();

  // Show year if range starts or ends in a date far from current
  if (
    Math.abs(now.diff(dateStart, "month")) > 3 ||
    Math.abs(now.diff(dateEnd, "month")) > 6
  ) {
    if (dateStart.year() === dateEnd.year()) {
      return `${dateStart.format("D MMMM")} - ${dateEnd.format("D MMMM YYYY")}`;
    }
    // prettier-ignore
    return `${dateStart.format("D MMMM YYYY")} - ${dateEnd.format("D MMMM YYYY")}`;
  }

  return `${dateStart.format("D MMMM")} - ${dateEnd.format("D MMMM")}`;
};

export function getMonthNameByIndex(index: number): string | undefined {
  if (langs) return langs.months[index];
  return undefined;
}

export function getCurrentDate() {
  return dayjs();
}
